import { Menu, MenuItem, Stack, Typography } from '@mui/material'
import { MouseEvent, useCallback, useState } from 'react'
import { Button, Icons, InformationModal } from 'utoniq-ui'

import {
  Artist,
  ConsoleDeliveryItemBoxFragment,
  DeliveryStatus,
  Maybe,
  MembershipSubscriptionProduct,
} from '@/clients/utoniq-core/schema'
import {
  ArtistHeaderItemsLeft,
  ArtistHeaderItemsLeftProps,
  ArtistHeaderItemsRight,
  BottomSafeArea,
  Head,
} from '@/components/layouts'
import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import { ArtistThemeProvider } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'

import { DeliveryManagementForm } from './DeliveryManagementForm'

export * from './fragments'

export type State = {
  artistId: string
  loading: boolean
  openConfirm: boolean
  merchArtistItemShipCompleting: boolean
  hasNextPage: boolean
  artist: Maybe<
    Pick<Artist, 'id' | 'paymentType' | 'hasArrangingDeliveryItems'>
  >
  user: Maybe<ArtistHeaderItemsLeftProps['currentUser']>
  deliveries: Maybe<{ edges: { node: ConsoleDeliveryItemBoxFragment }[] }>
  membership: Pick<MembershipSubscriptionProduct, 'productId'>[] | undefined
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onSetOpenConfirm: (value: boolean) => void
  onFetchMore: () => Promise<void>
  onFilter: (value?: DeliveryStatus) => Promise<void>
  onSubmit: (value: string[]) => Promise<void>
}

export const ConsoleDeliveryManagementTemplate: Template<State, Action> = ({
  state,
  onFetchMore,
  onSubmit,
  onFilter,
  onSetOpenConfirm,
}) => {
  const { t } = useTranslation('consoleDeliveryManagement')
  const { routerNames } = useRouter()
  const [disabledDeliveried, setDisabledDeliveried] = useState<boolean>(true)

  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <Head title={routerNames.ConsoleDeliveryManagement} />
      <ConsoleBaseLayout
        header={{
          left: (
            <ArtistHeaderItemsLeft
              currentUser={state.user}
              paymentType={state.artist?.paymentType}
              hasArrangingDeliveryItems={
                state.artist?.hasArrangingDeliveryItems
              }
              membership={state.membership}
            />
          ),
          right: <ArtistHeaderItemsRight artistId={state.artistId} />,
        }}
        customNavigation={
          !state.loading && !state.deliveries?.edges.length ? null : (
            <FixedButtonCell
              openConfirm={state.openConfirm}
              onSetOpenConfirm={onSetOpenConfirm}
              loading={state.merchArtistItemShipCompleting}
              disabled={disabledDeliveried}
            />
          )
        }
      >
        <HeaderFilterCell onFilter={onFilter} />
        {!state.loading && !state.deliveries?.edges.length ? (
          <Typography
            variant="body1"
            align="center"
            sx={{ py: 6 }}
            color="text.secondary"
          >
            {t('deliveryManagementEmpty')}
          </Typography>
        ) : (
          <DeliveryManagementForm
            loading={state.loading}
            hasNextPage={state.hasNextPage}
            deliveries={state.deliveries}
            onFetchMore={onFetchMore}
            onSubmit={onSubmit}
            setDisableDeliveried={setDisabledDeliveried}
          />
        )}
      </ConsoleBaseLayout>
    </ArtistThemeProvider>
  )
}

type HeaderFilterCellProps = {
  onFilter: (value?: DeliveryStatus) => Promise<void>
}
const HeaderFilterCell: Cell<HeaderFilterCellProps> = ({ onFilter }) => {
  const { t } = useTranslation('consoleDeliveryManagement')
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedIndex, setSelectedIndex] = useState(0)

  const options: {
    label: string
    value?: DeliveryStatus
  }[] = [
    { label: 'all', value: undefined },
    { label: DeliveryStatus.Shipped, value: DeliveryStatus.Shipped },
    { label: DeliveryStatus.Arranging, value: DeliveryStatus.Arranging },
  ]

  const handleFilterClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuItemClick = useCallback(
    (event: MouseEvent<HTMLElement>, newIndex: number) => {
      setSelectedIndex(newIndex)
      setAnchorEl(null)
    },
    []
  )

  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  return (
    <CellFrame sx={{ px: 5 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ py: 3 }}
      >
        <Typography variant="h3">{t('deliveryManagement')}</Typography>
        <Button
          variant="text"
          sx={{ width: 'fit-content', mr: -2 }}
          onClick={handleFilterClick}
        >
          <Typography variant="caption" sx={{ pr: 1 }}>
            {t(options[selectedIndex]['label'])}
          </Typography>
          <Icons.DownArrow fontSize="xSmall" sx={{ color: 'grey.600' }} />
        </Button>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
        >
          {options.map((option, index) => (
            <MenuItem
              sx={{ bgcolor: 'grey.50', minWidth: 200 }}
              key={index}
              selected={index === selectedIndex}
              onClick={event => {
                handleMenuItemClick(event, index)
                onFilter(option.value)
              }}
            >
              {t(option.label)}
            </MenuItem>
          ))}
        </Menu>
      </Stack>
      {/*<Stack sx={{ py: 2 }}>*/}
      {/*  <Search*/}
      {/*    value=""*/}
      {/*    fullWidth*/}
      {/*    placeholder={t('searchPlaceholder')}*/}
      {/*    onChange={e => {}}*/}
      {/*    size="small"*/}
      {/*    sx={{ minHeight: 40 }}*/}
      {/*  />*/}
      {/*</Stack>*/}
    </CellFrame>
  )
}

type FixedButtonCellProps = {
  loading: boolean
  disabled: boolean
  openConfirm: State['openConfirm']
  onSetOpenConfirm: Action['onSetOpenConfirm']
}
const FixedButtonCell: Cell<FixedButtonCellProps> = ({
  loading,
  disabled,
  openConfirm,
  onSetOpenConfirm,
}) => {
  const { t } = useTranslation('consoleDeliveryManagement')

  return (
    <BottomSafeArea sx={{ bgcolor: 'grey.50' }}>
      <CellFrame
        sx={{
          py: 4,
          display: 'flex',
          bgcolor: 'grey.50',
          justifyContent: 'center',
        }}
      >
        <Button
          size="large"
          disabled={disabled}
          onClick={() => onSetOpenConfirm(true)}
          sx={{ width: 244 }}
        >
          {t('markDelivered')}
        </Button>
        <InformationModal
          open={openConfirm}
          title={t('confirmMarkDeliveredTitle')}
          content={t('confirmMarkDeliveredDesc')}
          onClose={() => onSetOpenConfirm(false)}
          cancelText={t('cancel')}
          buttons={
            <Button
              size="large"
              loading={loading}
              sx={{ minWidth: 212 }}
              form="delivery-management-form"
              type="submit"
            >
              {t('ok')}
            </Button>
          }
        />
      </CellFrame>
    </BottomSafeArea>
  )
}
